import { useCallback, useState, useRef } from 'react';
import useAuthenticatedApi from '../../Authentication/components/hooks/useAuthenticatedApi';
import { Button } from '@/src/shadComponents/ui/button';
import { Progress } from '@/src/shadComponents/ui/progress';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/src/shadComponents/ui/dialog';
import {
    Upload,
    CheckCircle,
    XCircle,
    RotateCw,
    FileImage,
    X,
    Loader2,
} from 'lucide-react';
import { cn } from '@/src/lib/utils';
import { Badge } from '@/src/shadComponents/ui/badge';

interface FileStatus {
    file: File;
    status: 'pending' | 'uploading' | 'success' | 'error';
    error?: string;
}

export default function BulkUploadMedia({
    activationId,
    photoSessionId,
    onComplete,
    onCancel,
}: any) {
    const [files, setFiles] = useState<FileStatus[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    const [uploadedCount, setUploadedCount] = useState(0);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const { post } = useAuthenticatedApi();

    const addFiles = useCallback((newFiles: File[]) => {
        const fileStatuses: FileStatus[] = newFiles.map((file) => ({
            file,
            status: 'pending' as const,
        }));
        setFiles((prev) => [...prev, ...fileStatuses]);
    }, []);

    const removeFile = useCallback((index: number) => {
        setFiles((prev) => prev.filter((_, i) => i !== index));
    }, []);

    const uploadChunk = async (chunk: FileStatus[]) => {
        const formData = new FormData();
        for (const item of chunk) {
            formData.append('photo', item.file);
        }

        const response = await post(
            `/media/upload/${activationId}/bulk${
                photoSessionId ? `/${photoSessionId}` : ''
            }`,
            formData,
        );

        return response.status === 200;
    };

    const startUpload = async () => {
        const pendingFiles = files.filter(
            (f) => f.status === 'pending' || f.status === 'error',
        );
        if (pendingFiles.length === 0) return;

        setIsUploading(true);
        setUploadedCount(0);

        const chunkSize = 4;
        let completed = 0;

        // Mark all pending/error files as pending again
        setFiles((prev) =>
            prev.map((f) =>
                f.status === 'pending' || f.status === 'error'
                    ? { ...f, status: 'pending' as const, error: undefined }
                    : f,
            ),
        );

        for (let i = 0; i < pendingFiles.length; i += chunkSize) {
            const chunk = pendingFiles.slice(i, i + chunkSize);

            // Mark chunk as uploading
            setFiles((prev) =>
                prev.map((f) => {
                    if (chunk.some((c) => c.file === f.file)) {
                        return { ...f, status: 'uploading' as const };
                    }
                    return f;
                }),
            );

            try {
                const success = await uploadChunk(chunk);

                setFiles((prev) =>
                    prev.map((f) => {
                        if (chunk.some((c) => c.file === f.file)) {
                            return {
                                ...f,
                                status: success
                                    ? ('success' as const)
                                    : ('error' as const),
                                error: success
                                    ? undefined
                                    : 'Upload failed',
                            };
                        }
                        return f;
                    }),
                );

                if (success) {
                    completed += chunk.length;
                    setUploadedCount(completed);
                }
            } catch (error) {
                setFiles((prev) =>
                    prev.map((f) => {
                        if (chunk.some((c) => c.file === f.file)) {
                            return {
                                ...f,
                                status: 'error' as const,
                                error:
                                    error instanceof Error
                                        ? error.message
                                        : 'Network error',
                            };
                        }
                        return f;
                    }),
                );
            }
        }

        setIsUploading(false);

        // If all succeeded, notify parent
        const updatedFiles = files;
        const allSuccess = pendingFiles.every((pf) => {
            const current = updatedFiles.find((f) => f.file === pf.file);
            return current?.status === 'success';
        });
        if (allSuccess && completed === pendingFiles.length) {
            onComplete();
        }
    };

    const retryFailed = () => {
        setFiles((prev) =>
            prev.map((f) =>
                f.status === 'error'
                    ? { ...f, status: 'pending' as const, error: undefined }
                    : f,
            ),
        );
        startUpload();
    };

    const totalFiles = files.length;
    const successCount = files.filter((f) => f.status === 'success').length;
    const errorCount = files.filter((f) => f.status === 'error').length;
    const progress =
        totalFiles > 0 ? (uploadedCount / totalFiles) * 100 : 0;

    const handleDrop = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            setIsDragOver(false);
            const droppedFiles = Array.from(e.dataTransfer.files);
            if (droppedFiles.length > 0) {
                addFiles(droppedFiles);
            }
        },
        [addFiles],
    );

    return (
        <Dialog open={true} onOpenChange={() => !isUploading && onCancel()}>
            <DialogContent className="sm:max-w-xl">
                <DialogHeader>
                    <DialogTitle>Bulk Upload Media</DialogTitle>
                    <DialogDescription>
                        Upload photos and videos to this activation. Files are
                        uploaded in batches of 4.
                    </DialogDescription>
                </DialogHeader>

                {/* Drop zone */}
                <div
                    className={cn(
                        'border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer',
                        isDragOver
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/50',
                    )}
                    onDragOver={(e) => {
                        e.preventDefault();
                        setIsDragOver(true);
                    }}
                    onDragLeave={() => setIsDragOver(false)}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                >
                    <Upload className="size-8 mx-auto text-muted-foreground mb-3" />
                    <p className="text-sm font-medium text-foreground">
                        Drop files here or click to browse
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                        Supports images and videos
                    </p>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="*/*"
                        multiple
                        className="hidden"
                        onChange={(e) => {
                            if (e.target.files) {
                                addFiles(Array.from(e.target.files));
                                e.target.value = '';
                            }
                        }}
                    />
                </div>

                {/* File list */}
                {files.length > 0 && (
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-foreground">
                                {totalFiles} file{totalFiles !== 1 ? 's' : ''} selected
                            </span>
                            <div className="flex gap-2">
                                {successCount > 0 && (
                                    <Badge variant="default" className="bg-green-600">
                                        {successCount} uploaded
                                    </Badge>
                                )}
                                {errorCount > 0 && (
                                    <Badge variant="destructive">
                                        {errorCount} failed
                                    </Badge>
                                )}
                            </div>
                        </div>

                        {isUploading && (
                            <div className="space-y-1.5">
                                <Progress value={progress} className="h-2" />
                                <p className="text-xs text-muted-foreground text-center">
                                    {uploadedCount} of {totalFiles} uploaded
                                </p>
                            </div>
                        )}

                        <ul className="max-h-48 overflow-y-auto divide-y divide-border rounded-md border">
                            {files.map((item, index) => (
                                <li
                                    key={`${item.file.name}-${index}`}
                                    className="flex items-center gap-3 px-3 py-2 text-sm"
                                >
                                    <FileImage className="size-4 text-muted-foreground shrink-0" />
                                    <span className="truncate flex-1">
                                        {item.file.name}
                                    </span>
                                    <span className="text-xs text-muted-foreground shrink-0">
                                        {(item.file.size / 1024 / 1024).toFixed(1)} MB
                                    </span>
                                    {item.status === 'uploading' && (
                                        <Loader2 className="size-4 animate-spin text-primary shrink-0" />
                                    )}
                                    {item.status === 'success' && (
                                        <CheckCircle className="size-4 text-green-600 shrink-0" />
                                    )}
                                    {item.status === 'error' && (
                                        <XCircle className="size-4 text-destructive shrink-0" />
                                    )}
                                    {item.status === 'pending' && !isUploading && (
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                removeFile(index);
                                            }}
                                            className="text-muted-foreground hover:text-foreground"
                                        >
                                            <X className="size-4" />
                                        </button>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <DialogFooter className="gap-2">
                    {errorCount > 0 && !isUploading && (
                        <Button
                            variant="outline"
                            onClick={retryFailed}
                        >
                            <RotateCw className="size-4 mr-1.5" />
                            Retry Failed
                        </Button>
                    )}
                    <Button
                        variant="outline"
                        onClick={onCancel}
                        disabled={isUploading}
                    >
                        {successCount > 0 ? 'Done' : 'Cancel'}
                    </Button>
                    {files.length > 0 && errorCount !== totalFiles && (
                        <Button
                            onClick={startUpload}
                            disabled={
                                isUploading ||
                                files.filter(
                                    (f) =>
                                        f.status === 'pending' ||
                                        f.status === 'error',
                                ).length === 0
                            }
                        >
                            {isUploading ? (
                                <>
                                    <Loader2 className="size-4 animate-spin mr-1.5" />
                                    Uploading...
                                </>
                            ) : (
                                <>
                                    <Upload className="size-4 mr-1.5" />
                                    Upload{' '}
                                    {files.filter(
                                        (f) =>
                                            f.status === 'pending' ||
                                            f.status === 'error',
                                    ).length}{' '}
                                    Files
                                </>
                            )}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
